import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Checkbox,
} from "@material-ui/core";
import {
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  ShoppingCart as SalesIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  GetApp as ExportIcon,
  Settings as ManageIcon,
} from "@material-ui/icons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDate } from "../../hooks/useDate";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 0),
  },
  statsCard: {
    textAlign: "center",
    padding: theme.spacing(3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.main}05)`,
    border: `1px solid ${theme.palette.primary.main}20`,
    borderRadius: theme.spacing(2),
    transition: "transform 0.2s ease",
    "&:hover": {
      transform: "translateY(-2px)",
    },
  },
  statsIcon: {
    fontSize: "3rem",
    marginBottom: theme.spacing(1),
    opacity: 0.8,
  },
  statsNumber: {
    fontSize: "2.5rem",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
  },
  statsLabel: {
    color: theme.palette.text.secondary,
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  topProductsCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  productRow: {
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  productImage: {
    width: 40,
    height: 40,
    borderRadius: theme.spacing(1),
  },
  productName: {
    fontWeight: 500,
    marginBottom: 2,
  },
  productCategory: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  metricValue: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  refreshButton: {
    marginLeft: "auto",
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  performanceIndicator: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  positiveGrowth: {
    color: theme.palette.success.main,
  },
  negativeGrowth: {
    color: theme.palette.error.main,
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
  exportButton: {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.success.dark,
    },
  },
  manageButton: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.warning.dark,
    },
  },
}));

const CatalogDashboard = ({ stats, onRefresh }) => {
  const classes = useStyles();
  const { dateToClient } = useDate();
  
  const [topProducts, setTopProducts] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [topProductsRes, activityRes] = await Promise.all([
        api.get("/catalog/top-products"),
        api.get("/catalog/recent-activity"),
      ]);
      
      setTopProducts(topProductsRes.data.products || []);
      setRecentActivity(activityRes.data.activities || []);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
    onRefresh();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price / 100);
  };

  const handleExportReport = async (format = 'csv') => {
    try {
      setExporting(true);
      const response = await api.get(`/catalog/export?format=${format}`, {
        responseType: format === 'csv' ? 'blob' : 'json',
      });

      if (format === 'csv') {
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `produtos-relatorio-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        const dataStr = JSON.stringify(response.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `produtos-relatorio-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error("Error exporting report:", err);
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const handleManageFeatured = async (action) => {
    if (selectedProducts.length === 0) {
      alert("Selecione pelo menos um produto");
      return;
    }

    try {
      await api.post("/catalog/featured", {
        productIds: selectedProducts,
        action,
      });
      
      setSelectedProducts([]);
      loadDashboardData();
      onRefresh();
    } catch (err) {
      console.error("Error managing featured products:", err);
      toastError(err);
    }
  };

  const handleProductSelection = (productId, selected) => {
    if (selected) {
      setSelectedProducts(prev => [...prev, productId]);
    } else {
      setSelectedProducts(prev => prev.filter(id => id !== productId));
    }
  };

  const renderStatsCards = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent>
            <TrendingUpIcon className={classes.statsIcon} color="primary" />
            <Typography className={classes.statsNumber}>
              {stats.totalProducts}
            </Typography>
            <Typography className={classes.statsLabel}>
              Total de Produtos
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent>
            <ViewIcon className={classes.statsIcon} style={{ color: '#4caf50' }} />
            <Typography className={classes.statsNumber}>
              {stats.totalViews || 0}
            </Typography>
            <Typography className={classes.statsLabel}>
              Total de Visualizações
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent>
            <SalesIcon className={classes.statsIcon} style={{ color: '#ff9800' }} />
            <Typography className={classes.statsNumber}>
              {stats.totalSales || 0}
            </Typography>
            <Typography className={classes.statsLabel}>
              Total de Vendas
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid item xs={12} sm={6} md={3}>
        <Card className={classes.statsCard}>
          <CardContent>
            <StarIcon className={classes.statsIcon} style={{ color: '#e91e63' }} />
            <Typography className={classes.statsNumber}>
              {stats.featuredProducts || 0}
            </Typography>
            <Typography className={classes.statsLabel}>
              Produtos em Destaque
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderTopProducts = () => (
    <Paper className={classes.topProductsCard}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Produtos Mais Visualizados
        </Typography>
        <Box display="flex" alignItems="center">
          <Button
            variant="contained"
            size="small"
            className={`${classes.actionButton} ${classes.exportButton}`}
            startIcon={<ExportIcon />}
            onClick={() => handleExportReport('csv')}
            disabled={exporting}
          >
            Exportar CSV
          </Button>
          
          {selectedProducts.length > 0 && (
            <>
              <Button
                variant="contained"
                size="small"
                className={`${classes.actionButton} ${classes.manageButton}`}
                startIcon={<StarIcon />}
                onClick={() => handleManageFeatured('feature')}
              >
                Destacar ({selectedProducts.length})
              </Button>
              <Button
                variant="outlined"
                size="small"
                className={classes.actionButton}
                onClick={() => handleManageFeatured('unfeature')}
              >
                Remover Destaque
              </Button>
            </>
          )}
          
          <Tooltip title="Atualizar dados">
            <IconButton
              onClick={handleRefresh}
              disabled={loading}
              className={classes.refreshButton}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {topProducts.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">
            Nenhum dado de visualização disponível
          </Typography>
          <Typography variant="body2">
            Os dados aparecerão quando os produtos começarem a ser enviados
          </Typography>
        </div>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedProducts.length > 0 && selectedProducts.length < topProducts.length}
                    checked={topProducts.length > 0 && selectedProducts.length === topProducts.length}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedProducts(topProducts.map(p => p.id));
                      } else {
                        setSelectedProducts([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell>Produto</TableCell>
                <TableCell align="center">Visualizações</TableCell>
                <TableCell align="center">Vendas</TableCell>
                <TableCell align="center">Taxa de Conversão</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topProducts.map((product, index) => (
                <TableRow key={product.id} className={classes.productRow}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedProducts.includes(product.id)}
                      onChange={(e) => handleProductSelection(product.id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      <Avatar
                        src={product.imageUrl}
                        className={classes.productImage}
                        variant="rounded"
                      >
                        {product.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography className={classes.productName}>
                          {product.name}
                        </Typography>
                        <Typography className={classes.productCategory}>
                          {product.category} • {formatPrice(product.price)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.metricValue}>
                      {product.viewCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography className={classes.metricValue}>
                      {product.salesCount || 0}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box className={classes.performanceIndicator}>
                      <Typography className={classes.metricValue}>
                        {product.viewCount > 0 
                          ? ((product.salesCount || 0) / product.viewCount * 100).toFixed(1)
                          : 0}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                      <Chip
                        label={product.active ? 'Ativo' : 'Inativo'}
                        color={product.active ? 'primary' : 'default'}
                        size="small"
                      />
                      {product.featured && (
                        <StarIcon style={{ color: '#ffc107', fontSize: 16 }} />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  const renderRecentActivity = () => (
    <Paper className={classes.topProductsCard}>
      <Typography variant="h6" className={classes.sectionTitle}>
        Atividade Recente
      </Typography>
      
      {recentActivity.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="body1">
            Nenhuma atividade recente
          </Typography>
          <Typography variant="body2">
            As atividades do catálogo aparecerão aqui
          </Typography>
        </div>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ação</TableCell>
                <TableCell>Produto</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentActivity.map((activity, index) => (
                <TableRow key={index} className={classes.productRow}>
                  <TableCell>
                    <Typography variant="body2">
                      {activity.action}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {activity.productName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {dateToClient(activity.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.status}
                      color={activity.status === 'success' ? 'primary' : 'secondary'}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );

  return (
    <div className={classes.root}>
      {renderStatsCards()}
      
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          {renderTopProducts()}
        </Grid>
        <Grid item xs={12} lg={4}>
          {renderRecentActivity()}
        </Grid>
      </Grid>
    </div>
  );
};

export default CatalogDashboard;